import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import { z } from "zod";

extendZodWithOpenApi(z);

const statusesArray = [
	...([
		"FAILED",
		"FAILED_EXECUTION",
		"INCOMPLETE_DEPOSIT",
		"PENDING_DEPOSIT",
		"PROCESSING",
		"REFUNDED",
		"SUCCESS",
	] as const),
].sort();

type StatusType = (typeof statusesArray)[number];

function isStatus(status: unknown): status is StatusType {
	return typeof status === "string" && statusesArray.includes(status);
}

export const statusesVisibleToUserArray = [
	...([
		"FAILED",
		"INCOMPLETE_DEPOSIT",
		"PENDING_DEPOSIT",
		"PROCESSING",
		"REFUNDED",
		"SUCCESS",
	] as const),
].sort();

export type StatusTypeVisibleToUser =
	(typeof statusesVisibleToUserArray)[number];

export const statusesVisibleToUser: Record<StatusTypeVisibleToUser, string> = {
	PROCESSING: "Processing",
	SUCCESS: "Success",
	REFUNDED: "Refunded",
	FAILED: "Failed",
	PENDING_DEPOSIT: "Pending Deposit",
	INCOMPLETE_DEPOSIT: "Incomplete Deposit",
};

const statusesVisibleToUserToStatus = {
	PENDING_DEPOSIT: ["PENDING_DEPOSIT"],
	INCOMPLETE_DEPOSIT: ["INCOMPLETE_DEPOSIT"],
	PROCESSING: ["PROCESSING", "FAILED_EXECUTION"],
	SUCCESS: ["SUCCESS"],
	REFUNDED: ["REFUNDED"],
	FAILED: ["FAILED"],
} satisfies Record<StatusTypeVisibleToUser, StatusType[]>;

export function statusesVisibleToUserToStatuses(
	statuses: StatusTypeVisibleToUser[] | undefined,
): StatusType[] {
	return (
		statuses === undefined ? defaultStatuses : [...new Set(statuses)].sort()
	).flatMap((status) => {
		return statusesVisibleToUserToStatus[status];
	});
}

// No way to do it without assertion here
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const statusToStatusesVisibleToUser = Object.fromEntries(
	Object.entries(statusesVisibleToUserToStatus).flatMap(
		([status, statuses]) => {
			return statuses.map((s) => {
				return [s, status] as const;
			});
		},
	),
) as Record<StatusType, StatusTypeVisibleToUser>;

/**
 * We need to sort the statuses so that SUCCESS is first,
 * so this is what displayed as example in the UI.
 */
export const statusVisibleToUserEnum = z.enum(
	[...statusesVisibleToUserArray].sort((a, b) => {
		return a === "SUCCESS" && b !== "SUCCESS" ? -1 : 1;
	}),
);

export function mapStatus<T extends Record<string, unknown>>({
	status,
	...tx
}: T) {
	return isStatus(status)
		? { ...tx, status: statusToStatusesVisibleToUser[status] }
		: undefined;
}

export function mapStatuses<T extends Record<string, unknown>>(
	txs: T[],
): (T & { status: StatusTypeVisibleToUser })[] {
	return txs
		.map((tx) => {
			return mapStatus(tx);
		})
		.filter(Boolean);
}

export const statusDescriptions = {
	PENDING_DEPOSIT:
		"Quote created, but deposit not yet confirmed. Will move to 'Processing' if full deposit is received, or to 'Incomplete Deposit' if only a partial deposit is made.",
	INCOMPLETE_DEPOSIT:
		"Partial deposit received. Will move to 'Processing' once the full deposit is completed, or to 'Refunded' if the remaining deposit is not received before the deadline is reached.",
	PROCESSING:
		"Full deposit received, but swap is still in progress. Will move to 'Success' once the swap completes, or to 'Failed' or 'Refunded' if it does not.",
	SUCCESS: "Swap completed successfully.",
	REFUNDED: "Swap not completed, but deposit was refunded.",
	FAILED: "Swap not completed and deposit was not refunded.",
} as const satisfies Record<StatusTypeVisibleToUser, string>;

export function displayStatus(status: StatusTypeVisibleToUser) {
	return statusesVisibleToUser[status];
}

export const diffStatusesArray = ["failure", "success_value"] as const;

export type DiffStatus = (typeof diffStatusesArray)[number];

const DIFF_STATUSES: Record<DiffStatus, string> = {
	success_value: "Success",
	failure: "Failed",
};

export const diffStatusToStatusVisibleToUser: Record<
	DiffStatus,
	StatusTypeVisibleToUser
> = {
	success_value: "SUCCESS",
	failure: "FAILED",
};

function isKnownDiffStatus(status: string): status is DiffStatus {
	return status in DIFF_STATUSES;
}

export function statusesVisibleToUserToDiffStatuses(
	statuses: StatusTypeVisibleToUser[] | undefined,
): DiffStatus[] {
	return statusesVisibleToUserToStatuses(statuses)
		.map((status) => {
			if (status === "SUCCESS") {
				return "success_value";
			}

			if (status === "FAILED") {
				return "failure";
			}

			return null;
		})
		.filter(Boolean);
}

export function diffStatusToHumanReadable(status: string) {
	return isKnownDiffStatus(status)
		? DIFF_STATUSES[status]
		: status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
}

export const defaultStatuses = [
	...(["PROCESSING", "SUCCESS", "REFUNDED", "FAILED"] as const),
].sort();

export const statusesToTrack = new Set([
	"PENDING_DEPOSIT",
	"INCOMPLETE_DEPOSIT",
	"PROCESSING",
	"FAILED_EXECUTION",
] as const);

/** Raw and user-visible statuses alike, so either form can be checked. */
export const statusesWithHiddenAmounts: ReadonlySet<string> = new Set([
	"PENDING_DEPOSIT",
	"INCOMPLETE_DEPOSIT",
	"PROCESSING",
	"FAILED_EXECUTION",
] as const satisfies readonly StatusType[]);

/**
 * Check if amounts should be hidden for a given status for security reasons.
 * `withPublishableAmounts` zeroes the fields; the UI calls this to show the
 * hidden-amount tooltip and to blank the fees derived from those amounts.
 */
export function shouldHideAmountsForStatus(
	status: string,
	hasNearSwapTransaction: boolean,
): boolean {
	if (
		hasNearSwapTransaction &&
		(status === "PROCESSING" || status === "FAILED_EXECUTION")
	) {
		return false;
	}

	return statusesWithHiddenAmounts.has(status);
}
